import React, { useCallback, useContext, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { Trans, useTranslation } from "react-i18next";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { ScreenSize, UI } from "@wwimmo/ui";
import { Assembly, ASSEMBLY_STATE } from "src/stores/assemblies/AssemblyStore";
import { FilterStateType, SORT } from "src/stores/assemblies/AssembliesListStore";
import { AssemblyListRowDesktop } from "./AssemblyListRowDesktop";
import { AssemblyListRowMobile } from "./AssemblyListRowMobile";
import styles from "./AssembliesList.module.css";
import { Route, selectRoute } from "src/config/routes";
import { SCREEN_SIZE } from "src/stores/UIStore";
import { TagSearch } from "src/components/tagSearch/TagSearch";
import { v4 as uuidv4 } from "uuid";

interface AssembliesListProps {
    realestateId: string | undefined;
}

const AssembliesListHeaderBase = () => {
    const { t } = useTranslation();
    const { authStore, assemblyStore } = useContext(RootStoreContext);

    const onClick = useCallback(() => {
        assemblyStore.setIsNewAssembly(true);
        assemblyStore.setHasCurrentAssemblyLoaded(true);
    }, [assemblyStore]);

    if (
        authStore.user?.hasOnlyCoownerRole ||
        authStore.user?.hasOnlyCoownerAuditorRole ||
        assemblyStore.isManagerRealestateAssemblyRoute
    ) {
        return <React.Fragment />;
    }

    return (
        <UI.Row className="mb-1 assembly-list-header">
            <UI.Col xs={12} sm={{ span: 8 }}>
                <UI.Title>{t("screens.assemblies.sidebar_title")}</UI.Title>
            </UI.Col>

            <UI.Col xs={12} sm={12} md={4} lg={{ span: 4 }} className="d-grid">
                <UI.Button
                    to={selectRoute(Route.assemblyDetails, authStore.user?.role, {
                        assemblyid: uuidv4()
                    })}
                    label={t("screens.assemblies.new_assembly_button")}
                    className={`${styles.AddButton} w-100`}
                    onClick={onClick}
                />
            </UI.Col>
        </UI.Row>
    );
};
const AssembliesListHeader = observer(AssembliesListHeaderBase);

const AssembliesListSearchbarBase = () => {
    const { t } = useTranslation();
    const { authStore, assemblyStore, uiStore, assembliesListStore } = useContext(RootStoreContext);

    const onUpdateSearchTag = useCallback(
        (tagId: string, status: boolean) => {
            if (!authStore.user?.hasOnlyCoownerRole || !authStore.user?.hasOnlyCoownerAuditorRole) {
                assembliesListStore.setTag(tagId, status);
            }
        },
        [assembliesListStore, authStore.user?.hasOnlyCoownerRole, authStore.user?.hasOnlyCoownerAuditorRole]
    );

    const onChangeSearchQuery = useCallback(
        (query: string) => {
            assembliesListStore.setFilter({ ...assembliesListStore.assemblyFilter, query });
        },
        [assembliesListStore]
    );

    const onResetSearch = useCallback(() => {
        assembliesListStore.clearFilter();
    }, [assembliesListStore]);

    const onSortChange = useCallback(
        (newSort: SORT) => {
            assembliesListStore.setFilter({ ...assembliesListStore.assemblyFilter, sort: newSort });
        },
        [assembliesListStore]
    );

    const sortItems = useMemo(
        () => [
            { label: t("screens.tickets.sort.dateNewest"), value: SORT.DATE_NEWEST },
            { label: t("screens.tickets.sort.dateOldest"), value: SORT.DATE_OLDEST }
        ],
        [t]
    );

    return (
        <UI.Row className="assembly-list-searchbar-row">
            <UI.Col xs={12}>
                <div className={`${styles.Toolbar} mb-3`}>
                    <div className={styles.ToolbarTagSearch}>
                        <TagSearch
                            searchTags={assembliesListStore.searchTags}
                            value={assembliesListStore.assemblyFilter.query}
                            updateTagStatusById={onUpdateSearchTag}
                            onChangeInput={onChangeSearchQuery}
                            reset={onResetSearch}
                            searchStyles={
                                authStore.user?.hasOnlyCoownerRole ||
                                authStore.user?.hasOnlyCoownerAuditorRole ||
                                assemblyStore.isManagerRealestateAssemblyRoute
                                    ? "background-grey"
                                    : "background-white"
                            }
                        />
                    </div>
                    <div className={styles.ToolbarSort}>
                        <UI.Dropdown
                            id="sort"
                            onSelect={onSortChange}
                            items={sortItems}
                            label={
                                uiStore.isMobile
                                    ? ""
                                    : t(`screens.tickets.sort.${assembliesListStore.assemblyFilter.sort}`)
                            }
                            icon={uiStore.isMobile && <UI.Icon icon={UI.SVGIcon.Sort} />}
                        />
                    </div>
                </div>
            </UI.Col>
        </UI.Row>
    );
};
const AssembliesListSearchbar = observer(AssembliesListSearchbarBase);

const AssembliesListTabsOrDropdownBase = () => {
    const { t } = useTranslation();
    const { authStore, uiStore, assembliesListStore } = useContext(RootStoreContext);
    const hasOnlyCoownerOrAuditorRole = authStore.user?.hasOnlyCoownerRole || authStore.user?.hasOnlyCoownerAuditorRole;

    const onFilterChange = useCallback(
        (id: FilterStateType) => {
            if (assembliesListStore.assemblyFilter.state !== id) {
                assembliesListStore.setFilter({ ...assembliesListStore.assemblyFilter, state: id });
            }
        },
        [assembliesListStore]
    );

    const onTabsChange = useCallback(
        (id: string) => {
            onFilterChange(id as FilterStateType);
        },
        [onFilterChange]
    );

    const onSelectDropdown = useCallback(
        (id: string) => {
            onFilterChange(id as FilterStateType);
        },
        [onFilterChange]
    );

    if (hasOnlyCoownerOrAuditorRole) {
        return <React.Fragment />;
    }

    const filterStateItems = hasOnlyCoownerOrAuditorRole
        ? [
              {
                  label: `${t(
                      "screens.assemblies.states.agenda_published"
                  )} ${`(${assembliesListStore.assemblyStats.agendaPublished})`}`,
                  id: ASSEMBLY_STATE.AGENDA_PUBLISHED
              }
          ]
        : [
              {
                  label: `${t(
                      "screens.assemblies.states.preparing"
                  )} ${`(${assembliesListStore.assemblyStats.preparing})`}`,
                  id: ASSEMBLY_STATE.PREPARING
              },
              {
                  label: `${t(
                      "screens.assemblies.states.agenda_published"
                  )} ${`(${assembliesListStore.assemblyStats.agendaPublished})`}`,
                  id: ASSEMBLY_STATE.AGENDA_PUBLISHED
              },
              {
                  label: `${t(
                      "screens.assemblies.states.date_published"
                  )} ${`(${assembliesListStore.assemblyStats.datePublished})`}`,
                  id: ASSEMBLY_STATE.DATE_PUBLISHED
              },
              {
                  label: `${t("screens.assemblies.states.closed")} ${`(${assembliesListStore.assemblyStats.closed})`}`,
                  id: ASSEMBLY_STATE.CLOSED
              },
              {
                  label: `${t("labels.all")} ${`(${
                      assembliesListStore.assemblyStats.preparing +
                      assembliesListStore.assemblyStats.datePublished +
                      assembliesListStore.assemblyStats.agendaPublished +
                      assembliesListStore.assemblyStats.liveMode +
                      assembliesListStore.assemblyStats.closed
                  })`}`,
                  id: "All"
              }
          ];

    if (uiStore.isMobile) {
        const dropDownItems = filterStateItems.map((item) => ({ label: item.label, value: item.id }));
        const dropDownLabel =
            filterStateItems.find((item) => item.id === assembliesListStore.assemblyFilter.state)?.label ?? "";
        return (
            <UI.Dropdown
                id="sort"
                onSelect={onSelectDropdown}
                items={dropDownItems}
                label={dropDownLabel}
                className="mb-2"
            />
        );
    }

    return (
        <div className={styles.Tabbar}>
            <UI.Tabs
                selectedId={assembliesListStore.assemblyFilter.state}
                onTabChange={onTabsChange}
                contents={filterStateItems}
                nowrap
            ></UI.Tabs>
        </div>
    );
};
const AssembliesListTabsOrDropdown = observer(AssembliesListTabsOrDropdownBase);

const AssembliesListRendererBase = ({ realestateid }: { realestateid?: string }) => {
    const { t } = useTranslation();
    const { uiStore, assemblyStore, assembliesListStore, authStore } = useContext(RootStoreContext);

    const filterAssemblies = useCallback((): Assembly[] => {
        if (authStore.user?.hasOnlyCoownerRole || authStore.user?.hasOnlyCoownerAuditorRole) {
            return assembliesListStore.filteredAssemblies.filter((assembly) => {
                if (assembliesListStore.assemblyFilter.state === "All") {
                    return true;
                }
                return (
                    assembly.state === ASSEMBLY_STATE.AGENDA_PUBLISHED ||
                    assembly.state === ASSEMBLY_STATE.DATE_PUBLISHED ||
                    assembly.state === ASSEMBLY_STATE.PROTOCOL_PUBLISHED ||
                    assembly.state === ASSEMBLY_STATE.CLOSED
                );
            });
        } else {
            return assembliesListStore.filteredAssemblies.filter((assembly) => {
                if (assembliesListStore.assemblyFilter.state === "All") {
                    return true;
                }
                return assembly.state === assembliesListStore.assemblyFilter.state;
            });
        }
    }, [assembliesListStore, authStore]);

    useEffect(() => {
        assembliesListStore.updateSearchTags();
        if (!authStore.user?.hasOnlyCoownerRole || !authStore.user?.hasOnlyCoownerAuditorRole) {
            assembliesListStore.setRealestateUserData();
        }
    }, [
        assembliesListStore,
        filterAssemblies,
        authStore.user?.hasOnlyCoownerRole,
        authStore.user?.hasOnlyCoownerAuditorRole
    ]);

    const getColumnWidths = useCallback(() => {
        const columnWidthsDesktop = [0.5, 0.5, 1, 1, 0.5];
        const columnWidthsLaptopL = [0.4, 0.4, 0.8, 0.8, 0.4];
        const columnWidthsLaptop = [0.3, 0.3, 0.8, 0.8, 0.4];
        const columnWidthsTablet = [0.2, 0.2, 0.5, 0.5, 0];

        return uiStore.currentScreenSize > SCREEN_SIZE.LAPTOP_L
            ? columnWidthsDesktop
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP_L
            ? columnWidthsLaptopL
            : uiStore.currentScreenSize === SCREEN_SIZE.LAPTOP
            ? columnWidthsLaptop
            : columnWidthsTablet;
    }, [uiStore.currentScreenSize]);

    const columnWidths = getColumnWidths();

    const renderItem = useCallback(
        (assembly: Assembly, screenSize: ScreenSize) => {
            const columnWidths = getColumnWidths();

            const listRows = [
                screenSize === ScreenSize.MOBILE ? (
                    <AssemblyListRowMobile assembly={assembly} key={`row-${assembly.id}`} />
                ) : (
                    <AssemblyListRowDesktop assembly={assembly} key={`row-${assembly.id}`} widths={columnWidths} />
                )
            ];

            return (
                <UI.List.Item
                    key={`item-${assembly.id}`}
                    screenSize={screenSize}
                    rows={listRows}
                    className={styles.ListItem}
                    isClickableItemWithoutChevron
                    to={
                        assembly.id
                            ? assembly.state === ASSEMBLY_STATE.LIVEMODE_ACTIVE
                                ? selectRoute(Route.assemblyLiveMode, authStore.user?.role, {
                                      assemblyid: assembly.id
                                  })
                                : authStore.user?.hasOnlyCoownerRole || authStore.user?.hasOnlyCoownerAuditorRole
                                ? selectRoute(Route.coownerAssemblyDetails, authStore.user?.role, {
                                      realestateid: realestateid ?? "",
                                      assemblyid: assembly.id
                                  })
                                : selectRoute(Route.assemblyDetails, authStore.user?.role, {
                                      assemblyid: assembly.id
                                  })
                            : Route.assemblies
                    }
                />
            );
        },
        [
            getColumnWidths,
            authStore.user?.role,
            authStore.user?.hasOnlyCoownerRole,
            authStore.user?.hasOnlyCoownerAuditorRole,
            realestateid
        ]
    );

    const renderHeader = useCallback(
        (screenSize: ScreenSize) => {
            if (screenSize === ScreenSize.MOBILE) {
                return <></>;
            }
            const rows =
                authStore.user?.hasOnlyCoownerRole ||
                authStore.user?.hasOnlyCoownerAuditorRole ||
                assemblyStore.isManagerRealestateAssemblyRoute
                    ? [
                          <UI.List.Cell
                              key="date"
                              colspan={columnWidths[0]}
                              value={<Trans>screens.assemblies.list.date</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="address"
                              colspan={columnWidths[3]}
                              value={<Trans>screens.assemblies.list.place</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="status"
                              className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                              colspan={columnWidths[4]}
                              value={<Trans>screens.assemblies.list.status</Trans>}
                              truncate
                          />
                      ]
                    : [
                          <UI.List.Cell
                              key="date"
                              colspan={columnWidths[0]}
                              value={<Trans>screens.assemblies.list.date</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="number"
                              colspan={columnWidths[1]}
                              value={<Trans>screens.assemblies.list.number</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="realestate"
                              colspan={columnWidths[2]}
                              value={<Trans>screens.assemblies.list.realestate</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="address"
                              colspan={columnWidths[3]}
                              value={<Trans>screens.assemblies.list.place</Trans>}
                              truncate
                          />,
                          <UI.List.Cell
                              key="status"
                              className={`${uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? "d-none" : "d-flex"}`}
                              colspan={columnWidths[4]}
                              value={<Trans>screens.assemblies.list.status</Trans>}
                              truncate
                          />
                      ];
            return <UI.List.Header rows={rows} className="p-0" />;
        },
        [columnWidths, uiStore.currentScreenSize, authStore, assemblyStore.isManagerRealestateAssemblyRoute]
    );

    return (
        <UI.List.BasicList
            screenSize={uiStore.isMobile ? ScreenSize.MOBILE : ScreenSize.DESKTOP}
            items={filterAssemblies()}
            renderItem={renderItem}
            renderHeader={renderHeader}
            itemsPerPage={20}
            emptyListMessage={
                assemblyStore.assemblies.length === 0
                    ? t("screens.assemblies.list.no_assemblies_found").toString()
                    : t("screens.assemblies.list.assemblies_are_loading").toString()
            }
        />
    );
};
const AssembliesListRenderer = observer(AssembliesListRendererBase);

const AssembliesListBase = (props: AssembliesListProps) => {
    const { realestateId: realestateid } = props;
    const { uiStore, authStore, assemblyStore } = useContext(RootStoreContext);

    useEffect(() => {
        assemblyStore.reset();
        // eslint-disable-next-line
    }, []);

    return (
        <UI.Container
            className={
                (authStore.user?.hasOnlyCoownerRole || authStore.user?.hasOnlyCoownerAuditorRole
                    ? styles.AssembliesContainerCoowner
                    : assemblyStore.isManagerRealestateAssemblyRoute
                    ? styles.AssembliesContainerManagerRealestate
                    : styles.AssembliesContainer) +
                (uiStore.currentScreenSize <= SCREEN_SIZE.LAPTOP ? ` ${styles.MinPadding}` : "")
            }
            fluid
        >
            <AssembliesListHeader />
            <AssembliesListSearchbar />
            <UI.Row className="assemblies-list-row">
                <UI.Col xs={12}>
                    <UI.Card>
                        <AssembliesListTabsOrDropdown />
                        <AssembliesListRenderer realestateid={realestateid} />
                    </UI.Card>
                </UI.Col>
            </UI.Row>
        </UI.Container>
    );
};

export const AssembliesList = Sentry.withProfiler(observer(AssembliesListBase));
